import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { chakra, useFormControl } from "@chakra-ui/react";
import { useCombobox } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { matchSorter } from "match-sorter";
import { useFocusRing } from "react-aria";
import { SelectOption } from "./select-option";
import { SelectPopover, useSelectPopover } from "./select-popover";
import ClearSelectButton from "./clear-select-button";
import { SelectGroup } from "./select-group";
import { SelectOptionAccessory } from "./select-option-accessory";
import { SelectEmptyState } from "./select-empty-state";
import { SelectOptions } from "./select-options";
import { ComboboxControl } from "./combobox-control";
import { ComboboxInput } from "./combobox-input";
import { ComboboxToggleButton } from "./combobox-toggle-button";
import { Spinner } from "./spinner";
import { useVirtualItems } from "./hooks/use-virtual-items";
import { SelectOptionLabel } from "./select-option-label";
import { defaultCreateOptionMessage, defaultIsOptionDisabled, defaultOptionLabel, defaultOptionValue, } from "./lib/select-helpers";
// Used to identify a combobox option for creating a new option, e.g. "Create 'something'"
const CreatableOption = Symbol("creatable option");
export function GroupedCombobox({ isDisabled = false, isLoading = false, isInvalid = false, isClearable = false, isOptionDisabled = defaultIsOptionDisabled, emptyOptionsMessage = "No options", supportsCreatableOptions = false, createOptionMessage = defaultCreateOptionMessage, optionGroups, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, onCreateOption, }) {
    const [isCreating, setIsCreating] = useState(false);
    const inputRef = useRef(null);
    const formControl = useFormControl({
        isDisabled,
        isInvalid,
    });
    const selectedOption = useMemo(() => {
        if (!value) {
            return;
        }
        for (const optionGroup of optionGroups) {
            for (const option of optionGroup.options) {
                if (isEqual(optionValue(option), value)) {
                    return option;
                }
            }
        }
        // This `return` is indeed useless, but TypeScript errors without it
        // eslint-disable-next-line no-useless-return
        return;
    }, [optionGroups, value]);
    const [inputValue, setInputValue] = useState(() => {
        return selectedOption ? optionLabel(selectedOption) : "";
    });
    const [isFiltering, setIsFiltering] = useState(false);
    const filteredOptionGroups = useMemo(() => {
        if (!isFiltering || inputValue.length === 0) {
            return optionGroups;
        }
        return optionGroups
            .map((optionGroup) => {
            return {
                ...optionGroup,
                options: matchSorter(optionGroup.options, inputValue, {
                    keys: [optionLabel],
                }),
            };
        })
            .filter((optionGroup) => optionGroup.options.length > 0);
    }, [isFiltering, optionGroups, optionLabel, inputValue]);
    const downshiftItems = useMemo(() => {
        if (isCreating) {
            return [CreatableOption];
        }
        const items = [];
        for (const optionGroup of filteredOptionGroups) {
            items.push(...optionGroup.options);
        }
        return items;
    }, [isCreating, filteredOptionGroups]);
    const optionsContainer = useRef(null);
    const { virtualItems, getVirtualItemKey, getVirtualItemIndex } = useVirtualItems({
        optionGroups: isCreating ? [CreatableOption] : filteredOptionGroups,
        optionLabel,
    });
    const virtualizer = useVirtualizer({
        count: virtualItems.length,
        getItemKey: getVirtualItemKey,
        getScrollElement() {
            return optionsContainer.current;
        },
        estimateSize() {
            return 32;
        },
    });
    const popover = useSelectPopover();
    const combobox = useCombobox({
        isOpen: popover.isOpen,
        labelId: `${formControl.id}-label`,
        inputId: formControl.id,
        items: downshiftItems,
        itemToString(option) {
            if (!option) {
                return "";
            }
            if (typeof option === "symbol") {
                return inputValue;
            }
            return optionLabel(option);
        },
        inputValue,
        onInputValueChange({ inputValue }) {
            setInputValue(inputValue ?? "");
        },
        selectedItem: selectedOption ?? null,
        onSelectedItemChange({ selectedItem }) {
            // Selected item is a symbol, when user chooses to create a new option
            if (typeof selectedItem === "symbol") {
                if (typeof onCreateOption === "function") {
                    setIsCreating(false);
                    setInputValue("");
                    void onCreateOption(inputValue ?? "");
                }
                return;
            }
            if (selectedItem) {
                onChange(optionValue(selectedItem));
            }
            else if (isClearable) {
                onChange(undefined);
            }
            blurInput();
        },
        onIsOpenChange({ isOpen }) {
            if (isOpen) {
                popover.open();
            }
            else {
                popover.close();
            }
        },
        stateReducer(_state, { type, changes }) {
            if (type === useCombobox.stateChangeTypes.InputChange) {
                return {
                    ...changes,
                    highlightedIndex: 0,
                };
            }
            return changes;
        },
        onStateChange(changes) {
            if (changes.type === useCombobox.stateChangeTypes.InputChange) {
                setIsFiltering(true);
            }
            if (changes.type ===
                useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem ||
                changes.type === useCombobox.stateChangeTypes.InputBlur) {
                setIsFiltering(false);
            }
        },
        onHighlightedIndexChange({ highlightedIndex }) {
            if (typeof highlightedIndex !== "number") {
                return;
            }
            if (highlightedIndex === -1) {
                virtualizer.scrollToOffset(0);
                return;
            }
            const virtualIndex = getVirtualItemIndex(highlightedIndex);
            try {
                const [offset] = virtualizer.getOffsetForIndex(virtualIndex);
                virtualizer.scrollToOffset(offset);
            }
            catch (error) {
                // `getOffsetForIndex` throws when `useVirtualizer` hasn't measured an item
                // with `highlightedIndex` yet and there's nothing we can do, but silence it.
                // See https://github.com/hightouchio/hightouch-ui/issues/225.
                if (error instanceof Error &&
                    error.message === "Unexpected undefined") {
                    return;
                }
                throw error;
            }
        },
    });
    useEffect(() => {
        if (supportsCreatableOptions &&
            inputValue.length > 0 &&
            filteredOptionGroups.length === 0) {
            setIsCreating(true);
        }
    }, [supportsCreatableOptions, inputValue, filteredOptionGroups]);
    useEffect(() => {
        if (filteredOptionGroups.length > 0 || inputValue.length === 0) {
            setIsCreating(false);
        }
    }, [filteredOptionGroups]);
    // Reset combobox input value to selected option's label, if
    // user typed something else, but didn't select another option.
    useEffect(() => {
        if (popover.isOpen) {
            return;
        }
        if (combobox.selectedItem &&
            typeof combobox.selectedItem !== "symbol" &&
            optionLabel(combobox.selectedItem) !== inputValue) {
            setInputValue(optionLabel(combobox.selectedItem));
        }
    }, [popover.isOpen, inputValue, combobox.selectedItem, optionLabel]);
    // Reset combobox input value to empty string, if user hasn't
    // selected any option and there was no selected option before that.
    useEffect(() => {
        if (popover.isOpen) {
            return;
        }
        if (!combobox.selectedItem) {
            setInputValue("");
        }
    }, [popover.isOpen, combobox.selectedItem]);
    // Update popover position as user is typing, since filtering
    // can change the number of displayed options and as a result,
    // that can change the height of the popover
    useEffect(() => {
        popover.update();
    }, [inputValue]);
    const showClearButton = isClearable && Boolean(selectedOption);
    const accessory = selectedOption && typeof optionAccessory === "function"
        ? optionAccessory(selectedOption)
        : undefined;
    const { isFocusVisible, focusProps } = useFocusRing({ isTextInput: true });
    const inputProps = combobox.getInputProps({
        ref: inputRef,
        onFocus(event) {
            combobox.openMenu();
            formControl.onFocus(event);
            if (typeof focusProps.onFocus === "function") {
                focusProps.onFocus(event);
            }
        },
        onBlur(event) {
            formControl.onBlur(event);
            if (typeof focusProps.onBlur === "function") {
                focusProps.onBlur(event);
            }
        },
        onKeyDown(event) {
            if (event.key === "Escape" && popover.isOpen) {
                event.stopPropagation();
            }
        },
    });
    const blurInput = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.blur();
        }
    }, [inputRef]);
    return (React.createElement(RemoveScroll, { enabled: popover.isOpen },
        React.createElement(ComboboxControl, { "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], "aria-busy": isLoading ? "true" : undefined, "data-selected": value ? "true" : undefined, "data-disabled": formControl.disabled ? "true" : undefined, "data-focus-visible": isFocusVisible ? "true" : undefined, width: width, "data-testid": "grouped-combobox", ...combobox.getComboboxProps(popover.getTargetProps()) },
            accessory && React.createElement(SelectOptionAccessory, { accessory: accessory }),
            React.createElement(ComboboxInput, { ref: inputRef, placeholder: placeholder, disabled: formControl.disabled, "data-testid": "grouped-combobox-input", ...inputProps }),
            isLoading && React.createElement(Spinner, { size: "sm" }),
            !isLoading &&
                (showClearButton ? (React.createElement(ClearSelectButton, { isDisabled: formControl.disabled, onClick: combobox.reset })) : (React.createElement(ComboboxToggleButton, { disabled: formControl.disabled, ...combobox.getToggleButtonProps() })))),
        React.createElement(SelectPopover, { isOpen: popover.isOpen, "data-testid": "grouped-combobox-popover", ...combobox.getMenuProps(popover.getPopoverProps()) },
            downshiftItems.length === 0 && (React.createElement(SelectEmptyState, null, emptyOptionsMessage)),
            downshiftItems.length > 0 && (React.createElement(SelectOptions, { ref: optionsContainer, virtualizer: virtualizer, virtualItems: virtualItems }, (virtualItem, index) => {
                if (virtualItem.type === "group") {
                    return (React.createElement(chakra.div, { ref: virtualizer.measureElement, "data-index": index },
                        React.createElement(SelectGroup, null, virtualItem.label)));
                }
                if (virtualItem.type === "option") {
                    const accessory = optionAccessory?.(virtualItem.option);
                    return (React.createElement(SelectOption, { isSelected: virtualItem.option === selectedOption, "data-index": index, ...combobox.getItemProps({
                            ref: virtualizer.measureElement,
                            item: virtualItem.option,
                            index: virtualItem.index,
                            disabled: isOptionDisabled(virtualItem.option),
                        }) },
                        accessory && (React.createElement(SelectOptionAccessory, { accessory: accessory })),
                        React.createElement(SelectOptionLabel, null, optionLabel(virtualItem.option))));
                }
                if (virtualItem.type === "creatable-option") {
                    return (React.createElement(SelectOption, { isSelected: true, "data-index": 0, ...combobox.getItemProps({
                            ref: virtualizer.measureElement,
                            item: CreatableOption,
                            index: 0,
                        }) },
                        React.createElement(SelectOptionLabel, null, createOptionMessage(inputValue))));
                }
                return null;
            })))));
}
